import { PersonalRateDraft, PricelistTabDraft } from 'types/matters';
import { normalizePrice } from './matterPricelistEditorUtils';

/**
 * A following case's price exceptions, once per person: every list of its copy holds the same flagged rows, so the
 * first list naming a person gives their price.
 */
export function exceptionsOfLists(tabs: PricelistTabDraft[]): PersonalRateDraft[] {
    const exceptions = new Map<number, PersonalRateDraft>();
    tabs.forEach((tab) =>
        tab.personalRates
            .filter((row) => row.isException && !exceptions.has(row.employee.id))
            .forEach((row) => exceptions.set(row.employee.id, row))
    );
    return [...exceptions.values()];
}

/**
 * The followed periods as the case will hold them: each exception replaces its person's followed price in every
 * list, also one the followed list does not price them in. A flagged row of a person no longer excepted goes; the
 * caller puts their followed price back (withFollowedPrices).
 */
export function withExceptions(tabs: PricelistTabDraft[], exceptions: PersonalRateDraft[]): PricelistTabDraft[] {
    const excepted = new Set(exceptions.map((row) => row.employee.id));
    return tabs.map((tab) => ({
        ...tab,
        personalRates: [
            ...tab.personalRates.filter((row) => !row.isException && !excepted.has(row.employee.id)),
            ...exceptions.map((row) => ({ ...row, isException: true })),
        ],
    }));
}

/**
 * A stored copy holds no followed price for an excepted person, so a removed exception takes that person's rows of
 * the followed periods, matched by date.
 */
export function withFollowedPrices(
    tabs: PricelistTabDraft[],
    periods: PricelistTabDraft[],
    employeeIds: number[]
): PricelistTabDraft[] {
    const people = new Set(employeeIds);
    return tabs.map((tab) => {
        const period = periods.find((candidate) => candidate.validFrom === tab.validFrom);
        const restored = (period?.personalRates ?? []).filter((row) => people.has(row.employee.id));
        return {
            ...tab,
            personalRates: [...tab.personalRates.filter((row) => !people.has(row.employee.id)), ...restored],
        };
    });
}

/** The excepted people of the stored set that the staged set no longer holds. */
export function removedExceptionPeople(stored: PersonalRateDraft[], staged: PersonalRateDraft[]): number[] {
    const kept = new Set(staged.map((row) => row.employee.id));
    return stored.map((row) => row.employee.id).filter((id) => !kept.has(id));
}

/** An own copy prices every exception as an ordinary personal price. */
export function withoutExceptionFlags(tabs: PricelistTabDraft[]): PricelistTabDraft[] {
    return tabs.map((tab) => ({
        ...tab,
        personalRates: tab.personalRates.map(({ isException: _isException, ...row }) => row),
    }));
}

/** The people whose exception was added, re-priced or removed: their uninvoiced work is priced again. */
export function changedExceptionPeople(stored: PersonalRateDraft[], staged: PersonalRateDraft[]): number[] {
    const priceOf = (rows: PersonalRateDraft[]) =>
        new Map(rows.map((row) => [row.employee.id, normalizePrice(row.hourlyPrice)]));
    const before = priceOf(stored);
    const after = priceOf(staged);
    const people = new Set([...before.keys(), ...after.keys()]);
    return [...people].filter((id) => !before.has(id) || !after.has(id) || before.get(id) !== after.get(id));
}

export const hasUnpricedException = (exceptions: PersonalRateDraft[]): boolean =>
    exceptions.some((row) => normalizePrice(row.hourlyPrice) === null);
